import {Card, FormControl, FormField, Separator} from 'platform/components';
import {Box, Heading, HStack, Show, Text, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {UseFormReturn, useWatch} from 'react-hook-form';

import {head, isEmpty, isNil} from 'ramda';
import {isNotNil} from 'ramda-adjunct';

import {VatTypeSchema} from '@dms/api/shared';
import {CustomVatRateSource} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {useGetVatRatesOptions} from '@dms/shared';

import {Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {PRICING_FIELD_WIDTH} from '../constants/PRICING_FIELD_WIDTH';
import {DiscountSectionFormType} from '../schemas/discountSectionSchema';

interface CustomVatRateCardProps extends RequiredTestIdProps {
  control: FormControl<DiscountSectionFormType>;
  formApi: UseFormReturn<DiscountSectionFormType>;
  isReadOnly?: boolean;
  vatSource: CustomVatRateSource | Nullish;
}

const getVatSourceLabel = (vatSource: CustomVatRateSource) =>
  match(vatSource)
    .with('MANUAL', () => i18n.t('entity.order.vatSource.manual'))
    .with('ABROAD_SALE', () => i18n.t('entity.order.vatSource.abroadSale'))
    .with('FREE_OF_CHARGE', () => i18n.t('entity.order.vatSource.freeOfCharge'))
    .with('NON_TAX_DEDUCTIBLE', () => i18n.t('entity.order.vatSource.nonTaxDeductible'))
    .exhaustive();

export function CustomVatRateCard(props: CustomVatRateCardProps) {
  const isExpanded = useWatch({control: props.control, name: 'isCustomVatRateExpanded'});
  const [vatOptions] = useGetVatRatesOptions({fullLabel: true});

  // Preselects the first rate so the user does not land on an empty required dropdown.
  const handleExpandChange = (isEnabled: boolean) => {
    const firstVatType = VatTypeSchema.safeParse(head(vatOptions)?.value);

    if (isEnabled && isNil(props.formApi.getValues('customVatType')) && firstVatType.success) {
      props.formApi.setValue('customVatType', firstVatType.data, {shouldDirty: true});
    }
  };

  return (
    <Card variant="inlineGrey">
      <VStack spacing={4}>
        <VStack spacing={1}>
          <Heading size={4}>{i18n.t('entity.order.labels.customVatRate')}</Heading>
          <Text size="small" color="secondary">
            {i18n.t('entity.order.labels.customVatRateInfo')}
          </Text>
          <Show when={isNotNil(props.vatSource) ? props.vatSource : null}>
            {(vatSource) => (
              <Text size="small" color="secondary" data-testid={suffixTestId('vatSource', props)}>
                {`${i18n.t('entity.order.labels.customVatRateSource')}: ${getVatSourceLabel(vatSource)}`}
              </Text>
            )}
          </Show>
        </VStack>
        <Separator spacing={0} />
        <HStack align="center" spacing={3}>
          <FormField
            control={props.control}
            name="isCustomVatRateExpanded"
            type="switch"
            onChange={handleExpandChange}
            // Enabling waits for the rates, so the first one can be preselected.
            isDisabled={props.isReadOnly || (!isExpanded && isEmpty(vatOptions))}
            data-testid={suffixTestId('expand-switch', props)}
          />
          <Heading size={4}>{i18n.t('entity.order.labels.selectVatRate')}</Heading>
        </HStack>
        <Show when={isExpanded}>
          <Box width={PRICING_FIELD_WIDTH} maxWidth="100%">
            <FormField
              control={props.control}
              name="customVatType"
              type="choice"
              options={vatOptions}
              placeholder={i18n.t('general.labels.select')}
              menuInPortal
              isRequired
              isNotClearable
              isDisabled={props.isReadOnly}
              data-testid={suffixTestId('vatType', props)}
            />
          </Box>
        </Show>
      </VStack>
    </Card>
  );
}
